/**
 * Cart tier rewards + free gift.
 *
 * Two cooperating pieces, driven by snippets/free-gift-config.liquid (which emits
 * window.freeGiftConfig + window.freeGiftCart) and snippets/cart-milestones.liquid
 * (the server-rendered bar):
 *
 *  1. FreeGift — reconciles real gift line items against the cart subtotal. A $0 gift
 *     product (tagged with `_gift` line-item properties) is auto-added once a tier's
 *     threshold is met + the country qualifies, removed when the cart drops below it,
 *     and capped at quantity 1. Reconciles on page load, on every cart change, and on
 *     checkout click (the checkout trigger holds the submit until any in-flight reconcile
 *     settles so a removal racing the click can't carry a stale gift into checkout).
 *
 *  2. <cart-milestones> — recomputes the bar in the presentment currency using
 *     Shopify.currency.rate so it agrees with the gift logic under Markets, and reveals
 *     itself (the server hides it in non-base currencies to avoid a flash of pre-conversion
 *     values). Runs on every drawer re-render.
 *
 * Re-render is plumbed through the theme's own cart contract: request bundled sections via
 * `cart:prepare-bundled-sections`, POST them with the mutation, then dispatch `cart:change`
 * with a fresh full cart + the rendered sections (baseEvent 'free-gift:change' so <cart-drawer>
 * re-renders without auto-opening). Mirrors ProductForm / LineItem in theme.js.
 */
(function () {
  'use strict';

  var ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  var GIFT_PROP = '_gift';
  var MILESTONE_PROP = '_gift_milestone';
  var COUNTRIES_PROP = '_gift_countries';
  var DEBOUNCE = 300;
  var SELF_EVENT = 'free-gift:change';

  // --- Shopify money formatter (used only when a presentment conversion is in effect) ---
  // Mirrors Liquid's `money_without_trailing_zeros`, which the server-rendered bar uses:
  // a whole amount drops its decimals, a fractional one keeps them ($50.00 -> $50, $50.50 stays).
  function formatMoney(cents, format) {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    var formatString = format || '${{amount}}';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;

    function withDelimiters(number, precision, thousands, decimal) {
      precision = precision == null ? 2 : precision;
      thousands = thousands == null ? ',' : thousands;
      decimal = decimal == null ? '.' : decimal;
      if (isNaN(number) || number == null) return '0';
      number = (number / 100).toFixed(precision);
      var parts = number.split('.');
      var dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var rest = parts[1] ? decimal + parts[1] : '';
      return dollars + rest;
    }

    var match = formatString.match(placeholderRegex);
    var value = '';
    switch (match ? match[1] : 'amount') {
      case 'amount': value = withDelimiters(cents, 2); break;
      case 'amount_no_decimals': value = withDelimiters(cents, 0); break;
      case 'amount_with_comma_separator': value = withDelimiters(cents, 2, '.', ','); break;
      case 'amount_no_decimals_with_comma_separator': value = withDelimiters(cents, 0, '.', ','); break;
      case 'amount_with_apostrophe_separator': value = withDelimiters(cents, 2, "'", '.'); break;
      case 'amount_with_space_separator': value = withDelimiters(cents, 2, ' ', ','); break;
      case 'amount_no_decimals_with_space_separator': value = withDelimiters(cents, 0, ' '); break;
      default: value = withDelimiters(cents, 2);
    }
    // Anchor on the separator so a thousands group is never mistaken for decimals
    // ("1,234.00" -> "1,234", but "1,000" is left alone).
    value = value.replace(/([.,])00$/, '');
    return formatString.replace(placeholderRegex, value);
  }

  var FreeGift = {
    isProcessing: false,
    inflightReconcile: null,
    checkoutPending: false,

    config: function () {
      var c = window.freeGiftConfig;
      return c && c.enabled ? c : null;
    },

    isDesignMode: function () {
      return !!(window.Shopify && window.Shopify.designMode);
    },

    rate: function () {
      return (window.Shopify && window.Shopify.currency && Number(window.Shopify.currency.rate)) || 1;
    },

    thresholdCents: function (milestone) {
      return Math.round(milestone.thresholdCents * this.rate());
    },

    giftedMilestones: function () {
      var config = this.config();
      return config ? config.milestones.filter(function (m) { return m.variantId && m.available; }) : [];
    },

    countryEligible: function (milestone) {
      var allowed = milestone.allowedCountries || [];
      if (!allowed.length) return true;
      var country = this.config().currentCountry;
      return country ? allowed.indexOf(country) !== -1 : false;
    },

    // Sum of line totals for products excluded from the milestone calculation — subtracted
    // from the cart total so they count as if not in the cart. Mirrors cart-milestones.liquid.
    excludedCents: function (cart) {
      var config = this.config();
      var ids = (config && config.excludedProductIds) || [];
      if (!ids.length || !cart || !cart.items) return 0;
      var excluded = {};
      ids.forEach(function (id) { excluded[Number(id)] = true; });
      return cart.items.reduce(function (sum, item) {
        return excluded[Number(item.product_id)] ? sum + (item.final_line_price || 0) : sum;
      }, 0);
    },

    getCart: function () {
      return fetch(ROOT + 'cart.js', { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); });
    },

    postConfig: function (body) {
      return { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: body };
    },

    // Which sections want re-rendering — the theme's <cart-drawer> adds its own id here.
    bundledSections: function () {
      var set = new Set();
      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', { bubbles: true, detail: { sections: set } })
      );
      return Array.from(set);
    },

    // Diff the desired gift set (threshold met + country eligible) against the gift lines
    // already in the cart. Returns add / remove / clamp actions.
    buildActions: function (cart) {
      var self = this;
      // Same basis as the milestone bar: cart.total_price (post-discount, presentment) minus
      // any merchant-excluded products so they count as if not in the cart.
      var subtotal = Math.max(((cart && cart.total_price) || 0) - this.excludedCents(cart), 0);

      var desired = {};
      this.giftedMilestones().forEach(function (m) {
        if (subtotal >= self.thresholdCents(m) && self.countryEligible(m)) desired[m.id] = m;
      });

      var actions = [];
      var seen = {};
      (cart.items || []).forEach(function (item) {
        if (!item.properties || !item.properties[GIFT_PROP]) return;
        var owner = item.properties[MILESTONE_PROP] != null ? String(item.properties[MILESTONE_PROP]) : null;
        if (!owner || !desired[owner] || seen[owner]) {
          actions.push({ type: 'remove', key: item.key }); // not desired, orphan, or duplicate
          return;
        }
        seen[owner] = true;
        if ((item.quantity || 0) > 1) actions.push({ type: 'clamp', key: item.key });
      });

      Object.keys(desired).forEach(function (id) {
        if (!seen[id]) actions.push({ type: 'add', milestone: desired[id] });
      });

      return actions;
    },

    // Run one action, requesting cart sections so the response carries fresh HTML.
    runAction: function (action, sectionsParam) {
      var fields = sectionsParam ? { sections: sectionsParam, sections_url: window.location.pathname } : {};

      if (action.type === 'add') {
        var m = action.milestone;
        var properties = {};
        properties[GIFT_PROP] = 'true';
        properties[MILESTONE_PROP] = String(m.id);
        if (m.allowedCountries && m.allowedCountries.length) properties[COUNTRIES_PROP] = m.allowedCountries.join(',');
        var addBody = JSON.stringify(Object.assign({ id: m.variantId, quantity: 1, properties: properties }, fields));
        return fetch(ROOT + 'cart/add.js', this.postConfig(addBody))
          .then(function (r) {
            if (r.ok) return r.json();
            // Out of stock / not purchasable — skip, never block checkout.
            return r.json().then(function (e) { console.warn('[free-gift] could not add gift', m.variantId, e); return null; });
          })
          .catch(function (e) { console.warn('[free-gift] could not add gift', m.variantId, e); return null; });
      }

      // remove / clamp — target the unique line key so a paid copy is never touched.
      var quantity = action.type === 'clamp' ? 1 : 0;
      var changeBody = JSON.stringify(Object.assign({ id: action.key, quantity: quantity }, fields));
      return fetch(ROOT + 'cart/change.js', this.postConfig(changeBody))
        .then(function (r) { return r.ok ? r.json() : null; })
        .catch(function (e) { console.warn('[free-gift] could not update gift line', action.key, e); return null; });
    },

    // Single evaluation entry point used by all three triggers.
    evaluate: function (cart) {
      var self = this;
      if (!this.config() || this.isProcessing || this.isDesignMode()) return Promise.resolve(null);
      // A /cart/add.js response has no .items — fetch a full cart if needed.
      if (!cart || !cart.items) return this.getCart().then(function (fresh) { return self.evaluate(fresh); });

      var actions = this.buildActions(cart);
      if (!actions.length) return Promise.resolve(null);

      this.isProcessing = true;
      var sectionsParam = this.bundledSections().join(',');
      var lastSections = null;

      // Sequential — cart mutations aren't safe concurrently. Adds run last, so the final
      // response's sections reflect the end state.
      var chain = actions.reduce(function (previous, action) {
        return previous
          .then(function () { return self.runAction(action, sectionsParam); })
          .then(function (response) { if (response && response.sections) lastSections = response.sections; });
      }, Promise.resolve());

      this.inflightReconcile = chain
        .then(function () { return self.getCart(); })
        .then(function (freshCart) {
          self.isProcessing = false;
          self.inflightReconcile = null;
          // Re-render the cart UI (no auto-open). Prefer the rendered sections we already
          // have; fall back to cart:refresh if none were returned.
          if (lastSections) {
            freshCart.sections = lastSections;
            document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
              bubbles: true,
              detail: { baseEvent: SELF_EVENT, cart: freshCart }
            }));
          } else {
            document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
          }
          return freshCart;
        })
        .catch(function (e) {
          self.isProcessing = false;
          self.inflightReconcile = null;
          console.warn('[free-gift] reconcile failed', e);
          return null;
        });

      return this.inflightReconcile;
    },

    // Resolves once no reconcile is in flight (re-checks — one can start while another settles).
    whenSettled: function () {
      var self = this;
      return this.inflightReconcile ? this.inflightReconcile.then(function () { return self.whenSettled(); }) : Promise.resolve();
    },

    onCheckoutClick: function (event) {
      if (!this.config() || this.isDesignMode()) return;
      var btn = event.target.closest && event.target.closest('.js-free-gift-checkout');
      if (!btn || btn.dataset.freeGiftChecked) return;
      event.preventDefault();
      event.stopPropagation();
      if (this.checkoutPending) return; // spam clicks: first click's chain decides when to submit
      this.checkoutPending = true;
      var self = this;

      var submit = function () {
        self.checkoutPending = false;
        btn.dataset.freeGiftChecked = '1';
        var form = btn.form;
        if (form && form.requestSubmit) form.requestSubmit(btn);
        else btn.click();
      };

      // Wait for any in-flight reconcile, read fresh, reconcile, then re-loop while a reconcile
      // is still running. Capped so checkout is never blocked indefinitely.
      var attempt = function (tries) {
        return self.whenSettled()
          .then(function () { return self.getCart(); })
          .then(function (cart) { return self.evaluate(cart); })
          .then(function () {
            if (self.inflightReconcile && tries < 5) return attempt(tries + 1);
          });
      };
      attempt(0).then(submit).catch(submit);
    },

    init: function () {
      if (!this.config()) return;
      var self = this;

      // (a) page load — inline cart when present (evaluate fetches if it's missing).
      this.evaluate(window.freeGiftCart || null);

      // (b) any cart modification (debounced; ignore our own reconcile events).
      var timer = null;
      document.addEventListener('cart:change', function (event) {
        if (event && event.detail && event.detail.baseEvent === SELF_EVENT) return;
        var cart = event && event.detail && event.detail.cart;
        clearTimeout(timer);
        timer = setTimeout(function () { self.evaluate(cart && cart.items ? cart : null); }, DEBOUNCE);
      });

      // (c) checkout click — capture phase so we run before the native submit.
      document.addEventListener('click', function (event) { self.onCheckoutClick(event); }, true);
    }
  };

  FreeGift.init();

  // --- Milestone progress bar (presentment-currency recompute + reveal) ---
  if (!customElements.get('cart-milestones')) {
    customElements.define('cart-milestones', class extends HTMLElement {
      connectedCallback() {
        // Defer a tick so server-rendered children are present on upgrade.
        requestAnimationFrame(() => this.render());
      }

      render() {
        try {
          var checkpoints = Array.from(this.querySelectorAll('.js-cart-milestones-checkpoint'));
          if (!checkpoints.length) return;

          var rate = FreeGift.rate();
          // Only rewrite money text when converting, so single-currency stores stay pixel-identical.
          var convert = rate !== 1;
          var cartTotal = parseInt(this.dataset.cartTotal, 10) || 0;
          var format = (window.themeVariables && window.themeVariables.settings && window.themeVariables.settings.moneyFormat) || '${{amount}}';
          var segments = checkpoints.length + 1;

          var lastAchieved = -1;
          var nextIndex = -1;
          var nextThreshold = 0;
          var nextLabel = '';
          var allAchieved = true;

          checkpoints.forEach(function (checkpoint, i) {
            var threshold = Math.round((parseInt(checkpoint.dataset.thresholdCents, 10) || 0) * rate);
            var achieved = cartTotal >= threshold;
            checkpoint.classList.toggle('is-achieved', achieved);

            if (convert) {
              var amount = checkpoint.querySelector('.js-cart-milestones-amount');
              if (amount) amount.innerHTML = formatMoney(threshold, format);
            }

            if (achieved) {
              lastAchieved = i;
            } else {
              allAchieved = false;
              if (nextIndex === -1) {
                nextIndex = i;
                nextThreshold = threshold;
                nextLabel = checkpoint.dataset.label || '';
              }
            }
          });

          // Progress fill — mirrors the interpolation in cart-milestones.liquid.
          var progress = 100;
          if (!allAchieved) {
            var nextPosition = ((nextIndex + 1) * 100) / segments;
            var previousPosition = 0;
            var previousThreshold = 0;
            if (lastAchieved >= 0) {
              previousPosition = ((lastAchieved + 1) * 100) / segments;
              previousThreshold = Math.round((parseInt(checkpoints[lastAchieved].dataset.thresholdCents, 10) || 0) * rate);
            }
            var range = nextThreshold - previousThreshold;
            progress = range > 0
              ? previousPosition + (Math.max(cartTotal - previousThreshold, 0) / range) * (nextPosition - previousPosition)
              : nextPosition;
          }
          var fill = this.querySelector('.js-cart-milestones-fill');
          if (fill) fill.style.setProperty('--cart-milestones-progress', progress + '%');

          // Message — only when converting; otherwise the server text is correct.
          var config = FreeGift.config();
          var message = convert ? this.querySelector('.js-cart-milestones-message') : null;
          if (message && config) {
            if (allAchieved) {
              message.innerHTML = config.completedMessage || '';
            } else {
              var remaining = formatMoney(nextThreshold - cartTotal, format);
              message.innerHTML = (config.progressMessage || '')
                .split('{{ amount }}').join(remaining)
                .split('{{ next_label }}').join(nextLabel);
            }
          }
        } finally {
          this.style.opacity = '1';
        }
      }
    });
  }
})();
